import React, { useState, useEffect, useCallback } from 'react'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import {
    BookOpen, GraduationCap, Users, Star,
    ChevronRight, Search, Filter, Settings,
    BarChart3, RefreshCw
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

interface EduCategoryStats {
    id: string;
    name: string;
    description: string;
    icon: string;
    student_count: number;
    avg_mastery: number;
    is_visible: boolean;
    educational_level: string;
}

export const EducationManagement: React.FC = () => {
    const [stats, setStats] = useState<EduCategoryStats[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [levelFilter, setLevelFilter] = useState<string>('all')
    const supabase = createClientComponentClient()

    const fetchEduStats = useCallback(async () => {
        setLoading(true)
        try {
            // 1. Get all educational categories
            const { data: categories, error: catError } = await supabase
                .from('categories')
                .select('id, name, icon, description, is_visible, educational_level')
                .eq('is_educational', true)
                .order('name')

            if (catError) throw catError

            // 2. Get student progress counts and mastery averages for each
            const { data: progressData, error: progError } = await supabase
                .from('student_subject_progress')
                .select('category_id, mastery_score')

            if (progError) throw progError

            // Calculate stats
            const statsMap = (categories || []).map(cat => {
                const catProgress = (progressData || []).filter(p => p.category_id === cat.id);
                const studentCount = catProgress.length;
                const avgMastery = studentCount > 0
                    ? Math.round(catProgress.reduce((acc, p) => acc + Number(p.mastery_score || 0), 0) / studentCount)
                    : 0;

                return {
                    ...cat,
                    student_count: studentCount,
                    avg_mastery: avgMastery,
                    educational_level: cat.educational_level || 'all'
                } as EduCategoryStats;
            });

            setStats(statsMap)
        } catch (err) {
            console.error('Error fetching education stats:', err)
        } finally {
            setLoading(false)
        }
    }, [supabase])

    useEffect(() => {
        fetchEduStats()
    }, [fetchEduStats])

    const filteredStats = stats.filter(s => {
        const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesLevel = levelFilter === 'all' || s.educational_level === levelFilter;
        return matchesSearch && matchesLevel;
    })

    const levels = ['all', 'early', 'primary', 'secondary', 'post16', 'adult'];

    return (
        <div className="p-8 space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h2 className="text-3xl font-black text-slate-900 flex items-center gap-3">
                        <GraduationCap className="w-8 h-8 text-indigo-600" />
                        Education Hub Management
                    </h2>
                    <p className="text-slate-500 font-medium mt-1">
                        Monitor curriculum performance and student engagement
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={fetchEduStats}
                    disabled={loading}
                    className="flex items-center gap-2"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh Stats
                </Button>
            </div>

            <div className="flex flex-wrap gap-4 items-center bg-white/50 p-4 rounded-2xl border border-white/60">
                <div className="relative flex-1 min-w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                        placeholder="Search subjects..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="pl-10 bg-white/80 border-slate-200"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <select
                        value={levelFilter}
                        onChange={e => setLevelFilter(e.target.value)}
                        className="bg-white/80 border border-slate-200 rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/20"
                    >
                        {levels.map(l => (
                            <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className="h-48 bg-slate-100 rounded-[2rem]"></div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <AnimatePresence mode="popLayout">
                        {filteredStats.map((subject) => (
                            <motion.div
                                layout
                                key={subject.id}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                whileHover={{ y: -5 }}
                            >
                                <Card className="p-6 h-full border-white/60 bg-white/40 backdrop-blur-sm hover:shadow-xl hover:shadow-indigo-500/10 transition-all group">
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                                                {subject.icon || '📚'}
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-slate-900 text-lg leading-tight">{subject.name}</h3>
                                                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-700 mt-1">
                                                    {subject.educational_level}
                                                </span>
                                            </div>
                                        </div>
                                        <div className={`w-3 h-3 rounded-full ${subject.is_visible ? 'bg-emerald-500' : 'bg-slate-300'} shadow-sm`} />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        <div className="bg-white/60 p-3 rounded-2xl border border-white">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Students</p>
                                            <div className="flex items-center gap-2">
                                                <Users className="w-4 h-4 text-indigo-500" />
                                                <span className="text-xl font-black text-slate-900">{subject.student_count}</span>
                                            </div>
                                        </div>
                                        <div className="bg-white/60 p-3 rounded-2xl border border-white">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Mastery</p>
                                            <div className="flex items-center gap-2">
                                                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                                                <span className="text-xl font-black text-slate-900">{subject.avg_mastery}%</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="flex-1 rounded-xl text-xs font-bold hover:bg-white"
                                            onClick={() => window.open(`/category/${subject.id}`, '_blank')}
                                        >
                                            View Curriculum
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-10 h-10 p-0 rounded-xl hover:bg-white"
                                        >
                                            <Settings className="w-4 h-4 text-slate-400" />
                                        </Button>
                                    </div>
                                </Card>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            )}

            {filteredStats.length === 0 && !loading && (
                <div className="text-center py-20 bg-white/30 rounded-[3rem] border border-dashed border-slate-300">
                    <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-500 font-bold text-lg">No educational subjects found</p>
                    <p className="text-slate-400 text-sm">Try adjusting your search or filters</p>
                </div>
            )}
        </div>
    )
}
